/**
 * Stable error codes. Every error response carries `code`: a specific one from
 * this catalog when the API knows exactly what went wrong, otherwise the
 * default for its HTTP status. Codes never change meaning once shipped; new
 * ones may be added, so treat an unknown code like its status.
 */
export const API_ERROR_CODES = {
  // Defaults by status
  bad_request: 'The request is invalid.',
  unauthorized: 'Missing or invalid credentials.',
  payment_required: 'Payment is needed before this can go ahead.',
  forbidden: 'These credentials may not do this.',
  not_found: 'No such resource for these credentials.',
  conflict: 'The resource is not in a state that allows this.',
  gone: 'The resource existed but is no longer available.',
  payload_too_large: 'The request body is too large.',
  too_many_requests: 'Too many requests; slow down and retry later.',
  service_unavailable: 'A dependency is unavailable; retry later.',
  internal_error: 'Something failed on PixelSign’s side.',

  // Validation (see `errors[]` for each problem and its path)
  validation_failed: 'The request failed validation; see `errors`.',
  'validation.invalid_type': 'A value has the wrong type.',
  'validation.too_small': 'A value is too short, too small, or has too few items.',
  'validation.too_big': 'A value is too long, too large, or has too many items.',
  'validation.invalid_format': 'A string is not in the required format (email, URL, date, …).',
  'validation.invalid_value': 'A value is not one of the allowed values.',
  'validation.unrecognized_keys': 'An object has keys the API does not accept.',
  'validation.invalid_union': 'A value matches none of the allowed shapes.',
  'validation.custom': 'A value breaks a rule of the API.',

  // Envelope create (and preview)
  'payload.missing': 'The multipart `payload` part is missing.',
  'payload.invalid_json': 'The multipart `payload` part is not valid JSON.',
  'document.html_and_file': 'A document has both `html` and a PDF file part.',
  'document.missing_content': 'A document has neither `html` nor a PDF file part.',
  'document.too_large': 'A PDF exceeds the size limit.',
  'document.invalid_pdf': 'A file part is not a valid PDF.',
  'document.no_pages': 'A PDF has no pages.',
  'document.too_many_pages': 'A PDF has more pages than allowed.',
  'html.too_large': 'An HTML document exceeds the size limit.',
  'html.too_many_images': 'An HTML document has too many images.',
  'html.image_invalid': 'An HTML image is missing, empty, or not PNG, JPEG, GIF, or WebP.',
  'html.image_too_large': 'An HTML image exceeds the size limit.',
  'html.image_fetch_failed': 'An HTML image URL could not be fetched.',
  'html.marker_empty': 'A `data-ps-field` marker has no field name.',
  'html.marker_duplicate': 'A document has the same `data-ps-field` marker twice.',
  'html.markers_mismatch': 'A document’s `data-ps-field` markers don’t match its fields.',
  'field.unknown_document': 'A field names a document key that isn’t in `documents`.',
  'field.unknown_signer': 'A field names a signer key that isn’t in `signers`.',
  'field.missing_box': 'A field on a PDF needs page, x, y, width, and height.',
  'field.page_out_of_range': 'A field’s page is past the end of its document.',
  'field.duplicate_name': 'Two fields have the same name.',
  'field.date_signed_editable': '`date_signed` fields are always read-only.',
  'field.read_only_without_value': 'A read-only field needs a `value`.',
  'field.dropdown_options': 'A dropdown field needs at least two options.',
  'field.radio_group_missing': 'A radio field needs a `group`.',
  'field.radio_group_too_small': 'A radio group needs at least two options.',
  'field.radio_multiple': 'A radio group has more than one option selected.',
  'field.radio_value': 'A selected radio option must equal that option’s name.',
  'document.duplicate_key': 'Two documents have the same key.',
  'document.order_not_contiguous': 'Document `order` values must run 1…N.',
  'signer.duplicate_key': 'Two signers have the same key.',
  'signer.duplicate_email': 'Two signers have the same email.',
  'signer.duplicate_id': 'The same signer appears twice in the request.',
  'signer.expires_after_envelope': 'A signer’s `expiresAt` is after the envelope’s. Give the envelope a later deadline too.',
  'signer.expires_before_earlier_signer': 'A signer’s `expiresAt` is before the deadline of a signer earlier in the signing order, who could finish in time and still leave them expired.',
  'signer.notify_unused': 'A signer with `delivery: "caller"` can’t have `notify` copy: PixelSign sends them no invite or reminder.',
  'answerer.is_signer_self': 'A signer can’t answer their own questions.',
  'answerer.email_is_signer': 'An answerer’s email is a signer’s; use `answerer.signerKey`.',
  'signer.magic_address_live': '`@pixelsign.test` addresses work with test keys only.',
  'signer.magic_address_unknown': 'Unknown `@pixelsign.test` address.',
  'answerer.unknown_signer': 'An answerer names a signer key that isn’t in `signers`.',
  'envelope.expires_in_past': '`expiresAt` must be in the future.',

  // Preview
  'field.outside_page': 'Warning: a field’s box extends past the edge of its page.',
  'preview.unknown_document': '`document` names a key that isn’t in `documents`.',
  'preview.page_out_of_range': '`page` is past the end of the document.',

  // Sign-in, invitations, and access
  'auth.missing_credentials': 'No `Authorization: Bearer` header, or it is malformed.',
  'auth.session_invalid': 'The session token is expired, signed out, or not valid here; sign in again.',
  'auth.invalid_credentials': 'The email or password is wrong.',
  'auth.reset_link_invalid': 'The password reset link is invalid or has expired.',
  'auth.password_too_weak': 'The password doesn’t meet the requirements.',
  'invite.invalid': 'The invitation is invalid, expired, or already used.',
  'invite.not_found': 'No such pending invitation.',
  'role.insufficient': 'Your role in this organization doesn’t allow this.',
  'staff.admin_required': 'This needs the ADMIN staff role.',
  'view_as.read_only': 'Staff viewing an organization can only read.',
  'tenant.not_found': 'No such organization.',
  'tenant.suspended': 'The organization is suspended: its keys are disabled and its dashboard is read-only.',
  'tenant.slug_taken': 'Another organization already uses this slug.',
  'tenant.wrong_status': 'The organization is already in that state.',
  'tenant.closed': 'The organization has closed its PixelSign account.',
  'tenant.logo_unusable': 'The logo URL could not be fetched, or isn’t a PNG, JPEG, GIF, or WebP under 1 MB.',

  // Billing
  'billing.insufficient_credits': 'The organization’s credit is less than the price of a live envelope; an Owner can add credit.',
  'billing.exempt': 'PixelSign doesn’t bill this organization, so it can’t buy credit.',
  'billing.unavailable': 'Card payments aren’t set up on this PixelSign server.',
  'billing.purchase_not_found': 'No such credit purchase in this organization.',
  'billing.purchase_cancelled': 'The purchase was cancelled or expired before it was paid.',
  'billing.refund_too_large': 'The refund is more than the paid credit not yet spent.',
  'billing.card_required': 'Auto-reload needs a saved card; save one first.',
  'billing.card_setup_not_found': 'No such card setup in this organization.',
  'billing.price_out_of_order': 'A price change must take effect after every change already scheduled.',
  'billing.price_unchanged': 'That’s already the price at that time.',
  'billing.price_notice_too_short': 'A price increase must take effect at least 30 days after it’s scheduled.',
  'billing.refund_unavailable': 'Part of this credit can’t be refunded automatically (its payment is too old); contact PixelSign support.',

  // Team and keys
  'team.already_member': 'That person is already a member.',
  'team.member_not_found': 'No such member.',
  'team.last_owner': 'An organization must keep at least one owner.',
  'key.not_found': 'No such API key.',
  'key.invalid': 'The API key is not a valid PixelSign key for this environment.',
  'key.revoked': 'The API key has been revoked.',
  'key.already_revoked': 'The API key was already revoked.',
  'key.limit_reached': 'The organization has the most active keys allowed for this mode.',
  'key.live_requires_owner': 'Only owners can create or revoke live keys.',

  // Settings, webhooks, and test mail
  'url.not_allowed': 'A URL isn’t allowed: it must be https, public, and resolvable.',
  'settings.local_origin_in_live': 'Localhost embed origins are for test mode only.',
  'webhook.url_missing': 'Set a webhook URL for this mode first.',
  'webhook.no_previous_secret': 'No previous webhook secret is still signing deliveries.',
  'webhook.delivery_not_found': 'No such webhook delivery.',
  'listen.too_many': 'The organization has the most listen connections allowed.',
  'request.not_found': 'No such API request in the log.',
  'mail.not_found': 'No such captured email.',
  'allowlist.duplicate': 'That address or domain is already on the allowlist.',
  'allowlist.not_found': 'No such allowlist entry.',
  'allowlist.wrong_status': 'The allowlist entry isn’t in a state that allows this.',
  'allowlist.not_a_domain': 'Only domains are verified by DNS.',
  'allowlist.dns_record_missing': 'The DNS TXT record wasn’t found yet.',
  'allowlist.confirmation_invalid': 'The confirmation link is invalid or was already used.',

  // Envelopes
  'envelope.not_found': 'No envelope with this id for these credentials.',
  'envelope.wrong_status': 'The envelope’s status doesn’t allow this.',
  'envelope.expires_earlier': '`expiresAt` is earlier than the current deadline, the envelope’s or the signer’s. A deadline can only be extended.',
  'envelope.extend_empty': 'An extend names no deadline: give `expiresAt`, `signers`, or both.',
  'field.unsupported_characters': 'A value on a PDF document uses characters PixelSign can’t print there (it prints Latin, Greek, and Cyrillic). Draw a signature instead, or put the field on an HTML document.',
  'idempotency.key_reused': 'This Idempotency-Key already created a different envelope in the last 24 hours. Use a new key for a new envelope.',
  'idempotency.key_invalid': 'The Idempotency-Key header must be 1–256 printable ASCII characters.',
  'remind.no_active_signer': 'No signer is waiting to be reminded.',
  'remind.caller_delivered': 'This signer’s link is delivered by you (`delivery: "caller"`), so PixelSign doesn’t email them reminders.',
  'remind.daily_cap': 'The signer has no reminders left today.',
  'remind.max_reached': 'Every waiting signer already had the most reminders the policy allows.',
  'signer.not_found': 'No signer with this id on the envelope.',
  'signer.wrong_status': 'The signer has finished (signed, declined, or cancelled), so their deadline no longer applies.',
  'signer.not_their_turn': 'The signer can’t be given a link now: it isn’t their turn or they’ve finished, and they have no questions to answer or follow.',
  'signing.draft_conflict': 'The signing draft changed in another tab or window since this one last saved. Load the latest draft and save again.',
  'document.not_found': 'No document with this id on the envelope.',
  'asset.not_found': 'No asset with this id on the document.',
  'envelope.files_deleted': 'The envelope’s files were deleted (by you, retention, or test-data purge).',

  // Test mode
  'test_mode.required': 'This works with test keys only.',
  'simulate.not_their_turn': 'It isn’t this signer’s turn.',
  'simulate.unknown_field': 'A simulated value names a field the signer doesn’t have.',
  'simulate.wrong_value_type': 'A simulated value has the wrong type for its field.',

  // MCP (AI apps)
  'mcp.disabled': 'MCP isn’t available yet.',
  'mcp.org_unavailable': 'The organization isn’t available on this connection: you’re not a member, it was removed from the connection, or MCP is off for it.',
  'mcp.access_level_too_low': 'This connection’s access to the organization doesn’t allow this; reconnect the AI app and choose a higher level.',
  'mcp.org_setting_blocks': 'The organization’s MCP setting doesn’t allow this; an Owner can change it in the dashboard.',
  'mcp.staff_read_only': 'This staff connection is read-only; reconnect with “Make changes”.',
  'mcp.confirmation_unsupported': 'This AI app can’t show PixelSign’s confirmation, so this action isn’t available in it.',
  'mcp.confirmation_declined': 'The person declined the confirmation, so nothing was done.',
  'mcp.confirmation_timeout': 'The confirmation wasn’t answered in time, so nothing was done.',
  'mcp.not_available': 'This action isn’t available over MCP; use the dashboard.',
  'mcp.link_withheld': 'Live signing links are never returned over MCP; signers get theirs by email.',
  'mcp.allowance_exhausted': 'The organization’s MCP allowance for sends and reminders in this mode is used up until the time given.',
  'mcp.live_draft_only': 'Live envelopes are created as drafts over MCP; send it with send_envelope.',
  'mcp.live_origination_unavailable': 'Creating live envelopes over MCP isn’t switched on yet.',
  'mcp.request_in_progress': 'A request with this requestId is still running; retry shortly with the same requestId.',
  'mcp.request_id_reused': 'This requestId was already used for a different request; use a new one.',
  'mcp.request_outcome_unknown': 'A request with this requestId was interrupted while it ran, so whether it took effect is unknown; check, then use a new requestId if it didn’t.',
  'mcp.upload_not_found': 'No such upload, or it belongs to another connection, organization, or mode.',
  'mcp.upload_expired': 'The upload link, or the file uploaded to it, has expired.',
  'mcp.upload_used': 'The upload was already used, or already received a file.',
  'mcp.upload_invalid': 'The upload isn’t a PDF, or is larger than 20 MB.',
  'mcp.pdf_fetch_failed': 'PixelSign couldn’t fetch the PDF from the link.',
  'mcp.download_link_invalid': 'The download link is unknown, expired, or no longer allowed.',
  'mcp.consent_invalid': 'The connection request is unknown, expired, already finished, or for a different account.',
  'mcp.grant_code_required': 'This access needs the code PixelSign emailed you.',
  'mcp.grant_code_invalid': 'The emailed code is wrong or has expired.',
  'mcp.acknowledgement_required': 'Test + live access needs the acknowledgement that the app’s actions are your own.',
  'mcp.level_not_offered': 'That access level isn’t offered for this organization (your role, its setting, or an app PixelSign doesn’t recognize).',
  'mcp.connection_not_found': 'No such connection.',
  'mcp.recognized_app_invalid': 'A recognized app needs an exact https URL or custom-scheme redirect URI; addresses on this computer can’t be recognized.',
  'mcp.session_mismatch': 'The MCP session belongs to a different connection.',
} as const

export type ApiErrorCode = keyof typeof API_ERROR_CODES

export function isApiErrorCode(code: string): code is ApiErrorCode {
  return Object.hasOwn(API_ERROR_CODES, code)
}

/** The code an error gets from its HTTP status when nothing more specific applies. */
export function defaultErrorCode(status: number): ApiErrorCode {
  switch (status) {
    case 400:
      return 'bad_request'
    case 401:
      return 'unauthorized'
    case 402:
      return 'payment_required'
    case 403:
      return 'forbidden'
    case 404:
      return 'not_found'
    case 409:
      return 'conflict'
    case 410:
      return 'gone'
    case 413:
      return 'payload_too_large'
    case 429:
      return 'too_many_requests'
    case 503:
      return 'service_unavailable'
    default:
      return status >= 500 ? 'internal_error' : 'bad_request'
  }
}
